import { Link } from "react-router";
import { SectionHeading } from "@/components/bespoke/section-heading";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { auditExportPath } from "@/lib/api";

/** One setting row: what it is, and the control or badge that says where it stands. */
function Row({
  label,
  description,
  children,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{label}</p>
        {description ? <p className="mt-0.5 text-xs text-muted-foreground">{description}</p> : null}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/** A setting with no backend yet, so the row says so rather than pretending. */
function Planned() {
  return <Badge variant="muted">Planned</Badge>;
}

/**
 * Security. Every control here needs an identity or sandbox capability the server does not have
 * yet, so each row carries a Planned badge. The network allowlist lives in Integrations, and the
 * link says so.
 */
export function SecuritySection() {
  return (
    <section aria-label="Security" className="flex max-w-3xl flex-col gap-4">
      <div>
        <SectionHeading>Security</SectionHeading>
      </div>
      <Card>
        <CardContent className="flex flex-col">
          <Row label="Require single sign-on" description="Members must use an allowed provider.">
            <Planned />
          </Row>
          <Row label="Allowed sign-in domains" description="Which email domains may sign in.">
            <Planned />
          </Row>
          <Row label="Two-step verification for owners" description="Owners need a second factor.">
            <Planned />
          </Row>
          <Row label="Session length" description="How long a signed-in session lasts.">
            <Planned />
          </Row>
          <Row
            label="Scan for secrets"
            description="Configure scanning for run outputs and artefacts."
          >
            <Planned />
          </Row>
          <Row label="IP allowlist" description="Restrict access by source address.">
            <Planned />
          </Row>
          <Row
            label="Network allowlist"
            description="Configuration is not available in Integrations yet."
          >
            <Planned />
            <Link
              to="/integrations"
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Open in Integrations
            </Link>
          </Row>
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * Audit and export. The export downloads the same filtered range the audit page shows, so it
 * holds the server-side effect. Retention and SIEM streaming need capabilities the server does
 * not have yet, so those rows carry a Planned badge.
 */
export function AuditExportSection({ projectId }: { projectId: string }) {
  return (
    <section aria-label="Audit and export" className="flex max-w-3xl flex-col gap-4">
      <div>
        <SectionHeading>Audit and export</SectionHeading>
      </div>
      <Card>
        <CardContent className="flex flex-col">
          <Row label="Keep audit entries for" description="How long audit entries are retained.">
            <Planned />
          </Row>
          <Row
            label="Export the audit log"
            description="CSV of all audit entries available to this project."
          >
            <a
              href={auditExportPath(projectId, "all")}
              download
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Export CSV
            </a>
          </Row>
          <Row label="Stream to your SIEM" description="Forward audit events to a SIEM.">
            <Planned />
          </Row>
          <Row label="Open the audit log" description="Browse all audit entries.">
            <Link
              to="/audit"
              className="text-sm font-medium text-accent-text underline-offset-4 hover:underline"
            >
              Open the audit log
            </Link>
          </Row>
        </CardContent>
      </Card>
    </section>
  );
}

export function PlannedDataRetention() {
  return (
    <section aria-label="Additional retention settings">
      {["Run output retention", "Chat history retention", "Artefact retention"].map((label) => (
        <Row key={label} label={label}>
          <Planned />
        </Row>
      ))}
      <p className="mt-2 text-xs text-muted-foreground">
        Separate retention settings for these data types are not available yet. The evidence
        retention control above applies to its existing supported records.
      </p>
    </section>
  );
}

export function PlannedTokenSettings() {
  return (
    <section aria-label="Additional token settings">
      <Row label="Project API tokens">
        <Planned />
      </Row>
      <Row label="Inbound webhooks" description="Webhook configuration is not available yet.">
        <Planned />
        <a href="/automation" className="text-sm text-accent-text hover:underline">
          Open Automation
        </a>
      </Row>
    </section>
  );
}

export function PlannedNotificationDefaults() {
  return (
    <Row
      label="Project notification defaults"
      description="Personal preferences below do not change defaults for other members."
    >
      <Planned />
    </Row>
  );
}

export function PlannedPlanSettings() {
  return (
    <Row label="Subscription plan">
      <Planned />
    </Row>
  );
}
